import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { APP_BASE } from '@/lib/api';
import { createDefaultAboutPageContent } from '@/data/aboutPageDefaults';

const ADMIN_ABOUT_PAGE_URL = `${APP_BASE}/api/admin/settings/about-us.php`;

export default function AdminAboutUs() {
  const { token } = useAuth();
  const [content, setContent] = useState(() => createDefaultAboutPageContent());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchContent = async () => {
      setLoading(true);

      try {
        const response = await fetch(ADMIN_ABOUT_PAGE_URL, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error('Impossibile caricare la pagina Chi siamo');
        }

        const data = await response.json();
        if (!mounted) return;

        setContent({
          ...createDefaultAboutPageContent(),
          ...(data?.content || {}),
        });
      } catch (error) {
        toast.error(error.message || 'Errore caricamento pagina Chi siamo');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      mounted = false;
    };
  }, [token]);

  const updateField = (field, value) => {
    setContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const response = await fetch(ADMIN_ABOUT_PAGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(content),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || 'Errore nel salvataggio della pagina');
      }

      setContent({
        ...createDefaultAboutPageContent(),
        ...(data?.content || content),
      });
      toast.success('Pagina Chi siamo salvata correttamente');
    } catch (error) {
      toast.error(error.message || 'Errore nel salvataggio della pagina');
    } finally {
      setSaving(false);
    }
  };

  const fieldGroups = [
    {
      title: 'Hero pagina',
      description: 'Le scritte iniziali della pagina Chi siamo.',
      fields: [
        ['badge', 'Badge', 'input'],
        ['title', 'Titolo principale', 'input'],
        ['subtitle', 'Sottotitolo', 'textarea'],
        ['intro', 'Testo introduttivo', 'textarea'],
      ],
    },
    {
      title: 'Sezioni contenuto',
      description: 'I blocchi descrittivi della pagina.',
      fields: [
        ['story_title', 'Titolo storia', 'input'],
        ['story_text', 'Testo storia', 'textarea'],
        ['quality_title', 'Titolo cosa trovi da noi', 'input'],
        ['quality_text', 'Testo cosa trovi da noi', 'textarea'],
        ['service_title', 'Titolo come ti serviamo', 'input'],
        ['service_text', 'Testo come ti serviamo', 'textarea'],
      ],
    },
    {
      title: 'Punti forti',
      description: 'Titolo e tre card laterali della pagina.',
      fields: [
        ['values_title', 'Titolo punti forti', 'input'],
        ['value_1_title', 'Titolo card 1', 'input'],
        ['value_1_text', 'Testo card 1', 'textarea'],
        ['value_2_title', 'Titolo card 2', 'input'],
        ['value_2_text', 'Testo card 2', 'textarea'],
        ['value_3_title', 'Titolo card 3', 'input'],
        ['value_3_text', 'Testo card 3', 'textarea'],
      ],
    },
    {
      title: 'Blocco finale',
      description: 'Le ultime scritte con invito all’ordine.',
      fields: [
        ['cta_title', 'Titolo finale', 'input'],
        ['cta_text', 'Testo finale', 'textarea'],
      ],
    },
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pagina Chi siamo</h1>
          <p className="text-muted-foreground">
            Modifica i testi della pagina pubblica Chi siamo.
          </p>
        </div>

        <Button onClick={handleSave} disabled={loading || saving} className="gradient-instagram text-white">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {saving ? 'Salvataggio...' : 'Salva pagina'}
        </Button>
      </div>

      {loading ? (
        <Card className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </Card>
      ) : (
        <>
          {fieldGroups.map((group) => (
            <Card key={group.title} className="overflow-hidden border-[#eaded5]">
              <div className="border-b border-[#f1e4da] bg-[linear-gradient(135deg,#fff8f3_0%,#f8efe8_100%)] px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f3e6db] text-[#8f7f74]">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-foreground">{group.title}</h2>
                    <p className="text-sm text-muted-foreground">{group.description}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-5 px-6 py-6 lg:grid-cols-2">
                {group.fields.map(([field, label, type]) => (
                  <div key={field} className={type === 'textarea' ? 'lg:col-span-2 space-y-2' : 'space-y-2'}>
                    <Label htmlFor={field}>{label}</Label>
                    {type === 'textarea' ? (
                      <Textarea
                        id={field}
                        value={content[field] || ''}
                        onChange={(e) => updateField(field, e.target.value)}
                        className="min-h-[120px]"
                      />
                    ) : (
                      <Input
                        id={field}
                        value={content[field] || ''}
                        onChange={(e) => updateField(field, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
